
import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import {
    LockOutlined,
    VisibilityOutlined,
    VisibilityOffOutlined,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiForgotPassword, ApiResetPassword } from "../../Core/Apicall";
// Import your reset-password API here.

function ResetPassword() {
    const navigate = useNavigate();
    const location = useLocation();

    const { email, otp } = location.state || {};
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    const formik = useFormik({
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validationSchema: Yup.object({
            password: Yup.string()
                .min(8, "Password must be at least 8 characters")
                .required("New password is required"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("password")], "Passwords do not match")
                .required("Please confirm your password"),
        }),
        onSubmit: async (values) => {
            try {
                const payload = {
                    email,
                    password: values.password
                }
                const res = await ApiResetPassword(payload);
                if (!res?.status) throw new Error("Unable to reset password");

                toast.success("Password reset successfully!");
                navigate("/login", { replace: true });
            } catch (error: any) {
                toast.error(error?.message || "Failed to reset password");
            }
        },
    });

    React.useEffect(() => {
        if (!email || !otp) {
            navigate("/forgot-password", { replace: true });
        }
    }, [email, otp, navigate]);

    if (!email || !otp) return null;

    const passwordAdornment = (visible: boolean, toggle: () => void) => (
        <InputAdornment position="end">
            <IconButton onClick={toggle} edge="end">
                {visible ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Box className="p-3 p-lg-5 d-flex align-items-center justify-content-center h-100 w-100">
            <Box
                className="p-3 p-lg-5 d-flex flex-column w-100 border-10"
                sx={{
                    maxWidth: 700,
                    border: { xs: "none", md: "1px solid #ccc" },
                }}
            >
                <Typography variant="h5" className="text-center fw-bold">
                    Reset Password
                </Typography>

                <Typography className="text-center text-grey mt-2 mb-4">
                    Create a new password for your account.
                </Typography>

                <Box component="form" className="form-text-field" onSubmit={formik.handleSubmit}>
                    <Typography className="fw-bold mb-2">
                        New Password
                    </Typography>

                    <TextField
                        fullWidth
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlined />
                                    </InputAdornment>
                                ),
                                endAdornment: passwordAdornment(
                                    showPassword,
                                    () => setShowPassword((prev) => !prev)
                                ),
                            },
                        }}
                    />

                    <Typography className="fw-bold mb-2 mt-4">
                        Confirm Password
                    </Typography>

                    <TextField
                        fullWidth
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.confirmPassword &&
                            Boolean(formik.errors.confirmPassword)
                        }
                        helperText={
                            formik.touched.confirmPassword &&
                            formik.errors.confirmPassword
                        }
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlined />
                                    </InputAdornment>
                                ),
                                endAdornment: passwordAdornment(
                                    showConfirmPassword,
                                    () => setShowConfirmPassword((prev) => !prev)
                                ),
                            },
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        className="btn-main border-10 mt-4"
                        sx={{ py: 1.5 }}
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? "Resetting..." : "Reset Password"}
                    </Button>
                    
                </Box>
            </Box>
        </Box>
    );
}

export default ResetPassword;
